/**
 * VendorDB — a vendor master database (Crossroads, Hilti, …) on the Search page.
 * Two separate master collections per vendor (not the app's operational data):
 *  - Invoices master table (top): one row per uploaded vendor invoice —
 *    vendor, P.O., ship-to address, invoice date, invoice #, due date,
 *    total due — with a collapsed-by-default +/- expandable itemized
 *    materials sub-list (quantity, item, description, unit price, UOM, total)
 *    plus any extra header fields the vendor prints (account, order #, terms).
 *  - Account statements (below): one card per uploaded statement with every
 *    field from the file (vendor, account #, statement date, total due,
 *    remit-to, aging buckets) and its invoice rows. Invoice numbers that
 *    match a master invoice are clickable and jump to it.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, FileUp, Link2, Loader2, Lock, LockOpen, Minus, Plus, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import api from "@/lib/api";
import ScanMailButton from "./ScanMailButton";
import LinkInvoiceDialog from "./LinkInvoiceDialog";
import AuditDialog from "./AuditDialog";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQty = (n) => Number(n || 0).toLocaleString("en-CA", { maximumFractionDigits: 2 });

const AGING_LABELS = [
  ["current", "Current"], ["days_1_30", "1-30"], ["days_31_60", "31-60"],
  ["days_61_90", "61-90"], ["days_91_180", "91-180"], ["days_181_360", "181-360"], ["days_361_plus", "361+"],
];

const TH = "px-2.5 py-1.5 text-left font-mono text-[9px] uppercase tracking-widest text-white/50";
const TD = "px-2.5 py-1.5 text-xs text-white/85";

export default function VendorDB({ vendor, title }) {
  const [invoices, setInvoices] = useState([]);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [highlight, setHighlight] = useState(null);
  const [selInv, setSelInv] = useState(() => new Set());
  const [selStmt, setSelStmt] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(null); // "invoices" | "statements"
  const [linking, setLinking] = useState(null); // invoice row while the link-to-job popup is open
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [acct, setAcct] = useState("");
  const [acctLocked, setAcctLocked] = useState(true);
  const [acctSaving, setAcctSaving] = useState(false);
  const [autoOn, setAutoOn] = useState(false);
  const [autoTime, setAutoTime] = useState("00:01");
  const [autoLinkOn, setAutoLinkOn] = useState(false); // link new invoices to jobs by P.O. as they come in
  const fileRef = useRef(null);

  const saveConfig = async (over = {}) => {
    const body = { account_number: acct, auto_scan: autoOn, auto_scan_time: autoTime, auto_link: autoLinkOn, ...over };
    const { data } = await api.put(`/master/${vendor}/config`, body);
    setAcct(data.account_number);
    setAutoOn(data.auto_scan);
    setAutoTime(data.auto_scan_time);
    setAutoLinkOn(!!data.auto_link);
    return data;
  };

  const saveAutoLink = async (on) => {
    try {
      await saveConfig({ auto_link: on });
      toast.success(on
        ? `${title} auto-link on — every new invoice from a scan or import is linked to the job whose P.O. matches`
        : `${title} auto-link off`);
    } catch {
      toast.error("Could not save the auto-link setting");
    }
  };

  const saveAuto = async (on, time) => {
    try {
      await saveConfig({ auto_scan: on, auto_scan_time: time });
      toast.success(on
        ? `${title} auto-scan on — every day at ${time}, covering the previous 24 hours`
        : `${title} auto-scan off`);
    } catch {
      toast.error("Could not save the auto-scan setting");
    }
  };

  const toggleAcctLock = async () => {
    if (acctLocked) {
      setAcctLocked(false);
      return;
    }
    setAcctSaving(true);
    try {
      const data = await saveConfig();
      setAcctLocked(true);
      toast.success(data.account_number
        ? `${title} scan filter locked to account ${data.account_number}`
        : `${title} account filter cleared — scans accept any account`);
    } catch {
      toast.error("Could not save the account number");
    } finally {
      setAcctSaving(false);
    }
  };

  const toggleSet = (setter) => (id) =>
    setter((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const toggleSelInv = toggleSet(setSelInv);
  const toggleSelStmt = toggleSet(setSelStmt);

  const bulkDelete = async () => {
    const isInv = bulkConfirm === "invoices";
    const ids = [...(isInv ? selInv : selStmt)];
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post(`/master/${vendor}/bulk-delete`,
        isInv ? { invoice_ids: ids } : { statement_ids: ids });
      const n = isInv ? data.deleted_invoices : data.deleted_statements;
      toast.success(`Deleted ${n} ${isInv ? "invoice" : "statement"}${n === 1 ? "" : "s"}`);
      setSelInv(new Set());
      setSelStmt(new Set());
      setBulkConfirm(null);
      await load();
    } catch {
      toast.error("Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const load = async (silent = true) => {
    try {
      const [inv, stmt] = await Promise.all([
        api.get(`/master/${vendor}/invoices`),
        api.get(`/master/${vendor}/statements`),
      ]);
      setInvoices(inv.data);
      setStatements(stmt.data);
    } catch {
      if (!silent) toast.error(`Could not load the ${title} databases`);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, [vendor]);

  useEffect(() => {
    api.get(`/master/${vendor}/config`)
      .then((r) => {
        setAcct(r.data.account_number || "");
        setAutoOn(!!r.data.auto_scan);
        setAutoTime(r.data.auto_scan_time || "00:01");
        setAutoLinkOn(!!r.data.auto_link);
      })
      .catch(() => {});
  }, [vendor]);

  const onImport = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/master/${vendor}/import`, fd);
      (data.results || []).forEach((r) => {
        if (!r.ok) toast.error(`${r.file}: ${r.error}`);
        else if (r.doc_type === "invoice") toast.success(`${r.file}: invoice ${r.invoice_number} imported (${r.items} items)`);
        else toast.success(`${r.file}: statement imported (${r.rows} invoices listed)`);
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggle = (id) =>
    setExpanded((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  // statements are collapsed by default — only the header line shows until tapped
  const [openStmts, setOpenStmts] = useState(() => new Set());
  const toggleStmt = (id) =>
    setOpenStmts((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const unlinkedCount = invoices.filter((i) => !i.project_id).length;
  const stmtLinks = vendor !== "crossroads"; // Hilti: job links also on statement rows

  // Audit: statement lines with no imported invoice behind them
  const [auditing, setAuditing] = useState(false);
  const [audit, setAudit] = useState(null);
  const runAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await api.post(`/master/${vendor}/audit`);
      setAudit(data);
      if (!data.missing.length) toast.success(`All ${data.statement_invoices} statement invoices have a matching imported invoice`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally {
      setAuditing(false);
    }
  };
  const [autoLinking, setAutoLinking] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const rescan = async () => {
    if (!window.confirm(`Re-scan the ${unlinkedCount} unlinked invoice${unlinkedCount === 1 ? "" : "s"} with the current P.O. / jobsite rules, then auto-link? Linked invoices are left alone.`)) return;
    setRescanning(true);
    try {
      const { data } = await api.post(`/master/${vendor}/invoices/rescan`, { invoice_numbers: [] });
      const n = data.auto_link.linked.length;
      toast.success(`Re-scanned: ${data.changed} corrected (${data.reparsed} re-read by the model), ${data.unchanged} unchanged${n ? ` — ${n} auto-linked (${fmtMoney(data.auto_link.linked_total)})` : ""}`, { duration: 8000 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-scan failed");
    } finally { setRescanning(false); }
  };
  const autoLink = async () => {
    setAutoLinking(true);
    try {
      const { data } = await api.post(`/master/${vendor}/invoice-links/auto`);
      const n = data.linked.length;
      if (n) toast.success(`Linked ${n} invoice${n === 1 ? "" : "s"} (${fmtMoney(data.linked_total)}) to jobs by P.O.`);
      const left = data.no_match.length + data.ambiguous.length + data.no_po;
      if (left) {
        toast.info(`${left} still need a hand: ${data.no_po} with no P.O., ${data.no_match.length} with a P.O. that matches no job, ${data.ambiguous.length} where two jobs matched`, { duration: 8000 });
      } else if (!n) {
        toast.info("Nothing to link — every invoice already has a job");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto link failed");
    } finally {
      setAutoLinking(false);
    }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number} (${inv.vendor}) from the ${title} master table?`)) return;
    try {
      await api.delete(`/master/${vendor}/invoices/${inv.invoice_id}`);
      toast.success("Invoice deleted");
      await load();
    } catch {
      toast.error("Could not delete it");
    }
  };

  const deleteStatement = async (s) => {
    if (!window.confirm(`Delete the ${s.statement_date} statement (account ${s.account_number})?`)) return;
    try {
      await api.delete(`/master/${vendor}/statements/${s.statement_id}`);
      toast.success("Statement deleted");
      await load();
    } catch {
      toast.error("Could not delete it");
    }
  };

  // jump from a statement row to the matching master invoice
  const jumpToInvoice = (invoiceId) => {
    const el = document.getElementById(`master-invoice-row-${invoiceId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setExpanded((s) => new Set(s).add(invoiceId));
    setHighlight(invoiceId);
    setTimeout(() => setHighlight(null), 2500);
  };

  return (
    <div className="space-y-8" data-testid={`master-db-${vendor}`}>
      {/* header + import */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
          {title} · master database · vendor invoices & account statements
        </p>
        <div className="flex items-center gap-2">
          <ScanMailButton vendor={vendor} title={title} onDone={load} />
          <ScanMailButton vendor={vendor} title={title} onDone={load} mode="qb" />
          <input ref={fileRef} type="file" accept=".pdf,application/pdf" multiple className="hidden"
            onChange={(e) => onImport(e.target.files)} data-testid={`${vendor}-import-input`} />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${vendor}-import-btn`}
            className="flex items-center gap-2 rounded-md bg-[#1a3fd1] px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1633a3] disabled:opacity-50"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
            {uploading ? "Importing…" : "Import PDFs"}
          </button>
        </div>
      </div>

      {/* account lock: scans/imports only accept documents with this account number */}
      <div className="flex items-center gap-2" data-testid={`${vendor}-account-lock`}>
        <button
          type="button"
          onClick={toggleAcctLock}
          disabled={acctSaving}
          data-testid={`${vendor}-account-lock-btn`}
          title={acctLocked ? "Unlock to change the account number" : "Save and lock"}
          className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
            acctLocked
              ? "border-white/15 text-white/45 hover:border-white/30 hover:text-white/70"
              : "border-amber-500/50 bg-amber-500/10 text-amber-300"
          }`}
        >
          {acctSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : acctLocked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
        </button>
        <label className="font-mono text-[10px] uppercase tracking-widest text-white/40">Scan account #</label>
        <input
          value={acct}
          onChange={(e) => setAcct(e.target.value)}
          disabled={acctLocked}
          placeholder="no filter"
          data-testid={`${vendor}-account-input`}
          className={`w-36 rounded-md border px-2.5 py-1.5 font-mono text-xs transition-colors ${
            acctLocked
              ? "cursor-not-allowed border-white/10 bg-white/[0.03] text-white/40"
              : "border-amber-500/50 bg-white/[0.06] text-white focus:border-amber-400 focus:outline-none"
          }`}
        />
      </div>

      {/* auto scan: nightly, covers the 24 hours before the chosen time */}
      <div className="flex items-center gap-2" data-testid={`${vendor}-autoscan-row`}>
        <button
          type="button"
          role="switch"
          aria-checked={autoOn}
          onClick={() => saveAuto(!autoOn, autoTime)}
          data-testid={`${vendor}-autoscan-toggle`}
          className={`flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors ${autoOn ? "justify-end bg-[#1a3fd1]" : "justify-start bg-white/15"}`}
        >
          <span className="h-4 w-4 rounded-full bg-white" />
        </button>
        <label className="font-mono text-[10px] uppercase tracking-widest text-white/40">Auto scan</label>
        <input
          type="time"
          value={autoTime}
          disabled={!autoOn}
          onChange={(e) => setAutoTime(e.target.value)}
          onBlur={() => autoOn && saveAuto(true, autoTime)}
          data-testid={`${vendor}-autoscan-time`}
          className={`rounded-md border px-2 py-1 font-mono text-xs [color-scheme:dark] ${
            autoOn
              ? "border-white/20 bg-white/[0.05] text-white focus:border-[#4f6fe8] focus:outline-none"
              : "cursor-not-allowed border-white/10 bg-white/[0.03] text-white/35"
          }`}
        />
        <span className="text-[10px] text-white/30">scans the 24 hours before this time, every day</span>
        <span className="mx-1 hidden h-5 w-px bg-white/10 sm:block" aria-hidden="true" />
        <button
          type="button"
          role="switch"
          aria-checked={autoLinkOn}
          onClick={() => saveAutoLink(!autoLinkOn)}
          data-testid={`${vendor}-autolink-toggle`}
          className={`flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors ${autoLinkOn ? "justify-end bg-emerald-600" : "justify-start bg-white/15"}`}
        >
          <span className="h-4 w-4 rounded-full bg-white" />
        </button>
        <label className="font-mono text-[10px] uppercase tracking-widest text-white/40">Auto link</label>
        <span className="text-[10px] text-white/30">new invoices from scans and imports go to the job whose P.O. matches</span>
      </div>

      {loading && <p className="text-xs text-white/40" data-testid={`${vendor}-loading`}>Loading…</p>}

      {/* ================= account statements ================= */}
      <section>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-white/70" data-testid={`${vendor}-statements-heading`}>
            Account statements
            <span className="ml-2 text-white/35">{statements.length}</span>
          </h2>
          {statements.length > 0 && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={runAudit}
                disabled={auditing}
                data-testid={`${vendor}-audit`}
                title="Cross-check every statement line against the imported invoices"
                className="flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[11px] font-semibold text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
              >
                {auditing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                {auditing ? "Auditing…" : "Audit"}
              </button>
              <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-white/55">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-[#1a3fd1]"
                  checked={selStmt.size === statements.length && statements.length > 0}
                  onChange={() => setSelStmt(selStmt.size === statements.length ? new Set() : new Set(statements.map((s) => s.statement_id)))}
                  data-testid={`${vendor}-select-all-statements`}
                />
                All
              </label>
              {selStmt.size > 0 && (
                <button
                  type="button"
                  onClick={() => setBulkConfirm("statements")}
                  data-testid={`${vendor}-delete-selected-statements`}
                  className="flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-red-700"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete ({selStmt.size})
                </button>
              )}
            </div>
          )}
        </div>
        {statements.length === 0 && !loading && (
          <p className="rounded-lg border border-white/10 px-3 py-8 text-center text-xs text-white/35" data-testid={`${vendor}-statements-empty`}>
            No statements yet — import a {title} account statement PDF above.
          </p>
        )}
        <div className="space-y-4">
          {statements.map((s) => {
            const stmtOpen = openStmts.has(s.statement_id);
            return (
            <div key={s.statement_id} className="overflow-hidden rounded-lg border border-white/10" data-testid={`master-statement-${s.statement_id}`}>
              {/* statement header — every field from the file; tap anywhere to expand/collapse (collapsed by default) */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleStmt(s.statement_id)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleStmt(s.statement_id); } }}
                data-testid={`master-stmt-toggle-${s.statement_id}`}
                aria-expanded={stmtOpen}
                className={`flex cursor-pointer flex-wrap items-center gap-x-5 gap-y-1 bg-white/[0.04] px-3 py-2 text-xs text-white/70 hover:bg-white/[0.07] ${stmtOpen ? "border-b border-white/10" : ""}`}
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 shrink-0 accent-[#1a3fd1]"
                  checked={selStmt.has(s.statement_id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelStmt(s.statement_id)}
                  data-testid={`${vendor}-sel-stmt-${s.statement_id}`}
                  aria-label="Select statement"
                />
                <span className="text-white/40">{stmtOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</span>
                <span className="font-semibold text-white" data-testid={`master-stmt-vendor-${s.statement_id}`}>{s.vendor || "Statement"}</span>
                <span>Account <b>{s.account_number}</b></span>
                <span>Statement date <b>{s.statement_date}</b></span>
                <span>Total due <b className="text-[#fb923c]">{fmtMoney(s.total_amount_due)}</b></span>
                <span className="text-white/40">{(s.rows || []).length} invoices</span>
                {stmtOpen && s.remit_to && <span className="text-white/45">Remit to: {s.remit_to}</span>}
                <span className="ml-auto flex items-center gap-2">
                  {stmtOpen && <span className="text-white/35">{s.source_file}</span>}
                  <button type="button" onClick={(e) => { e.stopPropagation(); deleteStatement(s); }}
                    data-testid={`master-stmt-del-${s.statement_id}`}
                    aria-label="Delete statement" className="rounded p-1 text-white/25 hover:bg-red-500/15 hover:text-red-400">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
              {stmtOpen && (<>
              {/* aging buckets */}
              {s.aging && Object.keys(s.aging).length > 0 && (
                <div className="flex flex-wrap gap-3 border-b border-white/10 px-3 py-1.5" data-testid={`master-stmt-aging-${s.statement_id}`}>
                  {AGING_LABELS.map(([k, label]) => (
                    <span key={k} className="font-mono text-[10px] uppercase tracking-wider text-white/40">
                      {label} <b className="text-white/70">{fmtMoney(s.aging[k])}</b>
                    </span>
                  ))}
                </div>
              )}
              {/* invoice rows */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse">
                  <thead>
                    <tr className="bg-white/5">
                      <th className={TH}>Invoice #</th>
                      <th className={TH}>Ref#</th>
                      <th className={TH}>PO#</th>
                      {stmtLinks && <th className={TH}>Job</th>}
                      <th className={TH}>Trx date</th>
                      <th className={TH}>Due date</th>
                      <th className={`${TH} text-right`}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(s.rows || []).map((r, i) => (
                      <tr key={`${r.invoice_number}-${i}`} className="border-t border-white/5">
                        <td className={TD}>
                          {r.matched_invoice_id ? (
                            <button
                              type="button"
                              onClick={() => jumpToInvoice(r.matched_invoice_id)}
                              data-testid={`master-stmt-inv-${r.invoice_number}`}
                              title="Jump to the imported invoice"
                              className="flex items-center gap-1 font-semibold text-[#4f6fe8] underline decoration-dotted underline-offset-2 hover:text-white"
                            >
                              <Link2 className="h-3 w-3" />
                              {r.invoice_number}
                            </button>
                          ) : (
                            <span className="text-white/60" data-testid={`master-stmt-inv-${r.invoice_number}`}>{r.invoice_number}</span>
                          )}
                        </td>
                        <td className={TD}>{r.ref || "—"}</td>
                        <td className={TD}>{r.po || "—"}</td>
                        {stmtLinks && (
                          <td className={`${TD} whitespace-nowrap`}>
                            {/* Hilti keeps job links on statement rows (Crossroads links live on the invoices) */}
                            <button type="button"
                              onClick={() => setLinking({ invoice_number: r.invoice_number, amount: r.amount, trx_date: r.trx_date, po: r.po, ref: r.ref, statementId: s.statement_id, project_id: r.project_id, project_name: r.project_name })}
                              data-testid={`master-stmt-link-${r.invoice_number}`}
                              title={r.project_id ? `Linked to ${r.project_name} — tap to move or unlink` : "Link this invoice to a job"}
                              className={`inline-flex max-w-[160px] items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider transition-colors ${
                                r.project_id ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20" : "border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20"}`}>
                              <span className={`h-2 w-2 shrink-0 rounded-full ${r.project_id ? "bg-emerald-400" : "bg-red-500"}`} />
                              <span className="truncate">{r.project_id ? r.project_name : "link"}</span>
                            </button>
                          </td>
                        )}
                        <td className={`${TD} whitespace-nowrap`}>{r.trx_date}</td>
                        <td className={`${TD} whitespace-nowrap`}>{r.due_date}</td>
                        <td className={`${TD} text-right`}>{fmtMoney(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>)}
            </div>
          ); })}
        </div>
      </section>

{/* ================= invoices master table (summary of every invoice) ================= */}
      <section>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-white/70" data-testid={`${vendor}-invoices-heading`}>
            Invoices
            <span className="ml-2 text-white/35">{invoices.length}</span>
            {unlinkedCount > 0 && <span className="ml-2 text-red-300/80" data-testid={`${vendor}-invoices-unlinked`}>{unlinkedCount} unlinked</span>}
          </h2>
          <div className="flex items-center gap-2">
            {unlinkedCount > 0 && (
              <button
                type="button"
                onClick={autoLink}
                disabled={autoLinking}
                data-testid={`${vendor}-auto-link`}
                title="Link every unlinked invoice whose P.O. matches exactly one job"
                className="flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-semibold text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50"
              >
                {autoLinking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                {autoLinking ? "Linking…" : "Auto link"}
              </button>
            )}
            {unlinkedCount > 0 && (
              <button
                type="button"
                onClick={rescan}
                disabled={rescanning || autoLinking}
                data-testid={`${vendor}-rescan-unlinked`}
                title="Re-read every unlinked invoice with the current P.O. / jobsite rules (strips vendor branch codes, drops our own office address), then auto-link by P.O. or address"
                className="flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[11px] font-semibold text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
              >
                {rescanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                {rescanning ? "Re-scanning…" : "Re-scan"}
              </button>
            )}
          {selInv.size > 0 && (
            <button
              type="button"
              onClick={() => setBulkConfirm("invoices")}
              data-testid={`${vendor}-delete-selected-invoices`}
              className="flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-red-700"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete ({selInv.size})
            </button>
          )}
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-white/10" data-testid={`${vendor}-invoices-table`}>
          <table className="w-full min-w-[860px] border-collapse">
            <thead>
              <tr className="bg-white/5">
                <th className={`${TH} w-8`}>
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-[#1a3fd1]"
                    checked={invoices.length > 0 && selInv.size === invoices.length}
                    onChange={() => setSelInv(selInv.size === invoices.length ? new Set() : new Set(invoices.map((i) => i.invoice_id)))}
                    data-testid={`${vendor}-select-all-invoices`}
                    aria-label="Select all invoices"
                  />
                </th>
                <th className={`${TH} w-8`}></th>
                <th className={TH}>Vendor</th>
                <th className={TH}>P.O.</th>
                <th className={TH}>Job</th>
                <th className={TH}>Address</th>
                <th className={TH}>Invoice date</th>
                <th className={TH}>Invoice #</th>
                <th className={TH}>Due date</th>
                <th className={`${TH} text-right`}>Total due</th>
                <th className={`${TH} w-8`}></th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 && !loading && (
                <tr><td colSpan={11} className="px-3 py-8 text-center text-xs text-white/35" data-testid={`${vendor}-invoices-empty`}>
                  No invoices yet — import a {title} invoice PDF above.
                </td></tr>
              )}
              {invoices.map((inv) => {
                const open = expanded.has(inv.invoice_id);
                return [
                  <tr
                    key={inv.invoice_id}
                    id={`master-invoice-row-${inv.invoice_id}`}
                    data-testid={`master-invoice-row-${inv.invoice_id}`}
                    className={`border-t border-white/10 transition-colors ${highlight === inv.invoice_id ? "bg-[#1a3fd1]/25" : ""}`}
                  >
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-[#1a3fd1]"
                        checked={selInv.has(inv.invoice_id)}
                        onChange={() => toggleSelInv(inv.invoice_id)}
                        data-testid={`${vendor}-sel-inv-${inv.invoice_id}`}
                        aria-label="Select invoice"
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <button type="button" onClick={() => toggle(inv.invoice_id)}
                        data-testid={`master-expand-${inv.invoice_id}`}
                        aria-label={open ? "Collapse items" : "Expand items"}
                        className="rounded border border-white/15 p-0.5 text-white/60 hover:border-white/40 hover:text-white">
                        {open ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                      </button>
                    </td>
                    <td className={TD}>{inv.vendor}</td>
                    <td className={TD}>{inv.po_number || "—"}</td>
                    <td className={`${TD} whitespace-nowrap`}>
                      {/* red = not linked to a job yet; green = linked (expense sits in that job's unbilled list) */}
                      <button type="button"
                        onClick={() => setLinking({ invoice_number: inv.invoice_number, amount: inv.total_due, trx_date: inv.invoice_date, po: inv.po_number, ref: inv.order_number, project_id: inv.project_id, project_name: inv.project_name })}
                        data-testid={`master-invoice-link-${inv.invoice_id}`}
                        title={inv.project_id ? `Linked to ${inv.project_name} — tap to move or unlink` : "Link this invoice to a job"}
                        className={`inline-flex max-w-[160px] items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider transition-colors ${
                          inv.project_id ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20" : "border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20"}`}>
                        <span className={`h-2 w-2 shrink-0 rounded-full ${inv.project_id ? "bg-emerald-400" : "bg-red-500"}`} />
                        <span className="truncate">{inv.project_id ? inv.project_name : "link"}</span>
                      </button>
                    </td>
                    <td className={`${TD} max-w-[220px] truncate`} title={inv.ship_to_address}>{inv.ship_to_address || "—"}</td>
                    <td className={`${TD} whitespace-nowrap`}>{inv.invoice_date || "—"}</td>
                    <td className={`${TD} font-semibold`} data-testid={`master-invoice-num-${inv.invoice_id}`}>{inv.invoice_number}</td>
                    <td className={`${TD} whitespace-nowrap`}>{inv.due_date || "—"}</td>
                    <td className={`${TD} text-right font-semibold`}>{fmtMoney(inv.total_due)}</td>
                    <td className="px-2 py-1.5">
                      <button type="button" onClick={() => deleteInvoice(inv)}
                        data-testid={`master-invoice-del-${inv.invoice_id}`}
                        aria-label="Delete invoice" className="rounded p-1 text-white/25 hover:bg-red-500/15 hover:text-red-400">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>,
                  open && (
                    <tr key={`${inv.invoice_id}-items`} className="border-t border-white/5 bg-white/[0.02]">
                      <td></td>
                      <td></td>
                      <td colSpan={9} className="px-2.5 py-2">
                        {(inv.customer_account || inv.order_number || inv.payment_terms) && (
                          <p className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-white/40" data-testid={`master-invoice-extra-${inv.invoice_id}`}>
                            {[
                              inv.customer_account && `Account ${inv.customer_account}`,
                              inv.order_number && `Order ${inv.order_number}`,
                              inv.payment_terms && `Terms ${inv.payment_terms}`,
                            ].filter(Boolean).join(" · ")}
                          </p>
                        )}
                        <table className="w-full border-collapse" data-testid={`master-invoice-items-${inv.invoice_id}`}>
                          <thead>
                            <tr className="text-white/40">
                              <th className={`${TH} text-right`}>Quantity</th>
                              <th className={TH}>Item</th>
                              <th className={TH}>Description</th>
                              <th className={`${TH} text-right`}>Unit price</th>
                              <th className={TH}>UOM</th>
                              <th className={`${TH} text-right`}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(inv.items || []).map((it, i) => (
                              <tr key={i} className="border-t border-white/5">
                                <td className={`${TD} text-right`}>{fmtQty(it.quantity)}</td>
                                <td className={`${TD} font-mono`}>{it.item}</td>
                                <td className={TD}>{it.description}</td>
                                <td className={`${TD} text-right`}>{it.unit_price}</td>
                                <td className={TD}>{it.uom}</td>
                                <td className={`${TD} text-right`}>{fmtMoney(it.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* -------- bulk delete confirmation popup -------- */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4" onClick={() => !bulkDeleting && setBulkConfirm(null)}>
          <div
            className="w-full max-w-sm rounded-xl border border-white/15 bg-[#0d0d0d] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${vendor}-bulk-confirm-dialog`}
          >
            <h3 className="text-sm font-bold text-white">
              Delete {bulkConfirm === "invoices" ? selInv.size : selStmt.size} {bulkConfirm === "invoices" ? "invoice" : "statement"}
              {(bulkConfirm === "invoices" ? selInv.size : selStmt.size) === 1 ? "" : "s"}?
            </h3>
            <p className="mt-2 text-xs text-white/50">
              This permanently removes {(bulkConfirm === "invoices" ? selInv.size : selStmt.size) === 1 ? "it" : "them"} from the {title} master database. This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setBulkConfirm(null)}
                disabled={bulkDeleting}
                data-testid={`${vendor}-bulk-cancel`}
                className="rounded-md border border-white/15 px-4 py-2 text-xs font-semibold text-white/60 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={bulkDelete}
                disabled={bulkDeleting}
                data-testid={`${vendor}-bulk-confirm`}
                className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {bulkDeleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {bulkDeleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
      {audit && <AuditDialog vendor={vendor} title={title} audit={audit} onClose={() => setAudit(null)} />}
      {linking && (
        <LinkInvoiceDialog vendor={vendor} row={linking} statementId={linking.statementId} onClose={() => setLinking(null)}
          onLinked={(res) => {
            // flip the bullet everywhere this invoice number appears (invoice row + Hilti statement rows), no refetch needed
            const patch = (x) => ({ ...x, project_id: res?.project_id || null, project_name: res?.project_name || null, expense_id: res?.expense_id || null });
            setInvoices((list) => list.map((x) => (x.invoice_number === linking.invoice_number ? patch(x) : x)));
            if (stmtLinks) {
              setStatements((list) => list.map((st) => ({ ...st, rows: (st.rows || []).map((x) => (x.invoice_number === linking.invoice_number ? patch(x) : x)) })));
            }
          }} />
      )}

          </div>
  );
}
